/**
 * Recovery capture for Stripe Elements checkout (/checkout-channel-invite).
 * Fire-and-forget — never blocks UX.
 */

import {
  captureVisitorJourneyRealtime,
  getJourneyPayloadForServer,
  getPostHogIdsForServer,
  setJourneyRecoveryEmail,
} from "@/lib/posthogVisitorJourney";

function canPostApi(): boolean {
  if (typeof window === "undefined") return false;
  if (!import.meta.env.DEV) return true;
  const proxy = (import.meta.env.VITE_DEV_API_PROXY as string | undefined)?.trim();
  return Boolean(proxy);
}

export type StripeCheckoutCaptureOpts = {
  email: string;
  packId: string;
  country?: string;
  language?: "es" | "en";
  amountCents?: number;
  currency?: string;
  paymentIntentId?: string;
  landingPath?: string;
  flowName?: string;
  productName?: string;
};

export function postStripeCheckoutAbandonmentCapture(
  opts: StripeCheckoutCaptureOpts,
): void {
  if (!canPostApi()) return;
  const email = opts.email.trim().toLowerCase();
  if (!email.includes("@")) return;

  setJourneyRecoveryEmail(email);

  const flowName =
    opts.flowName ??
    (opts.packId === "channel_invite" ? "channel_invite" : "stripe_checkout");

  captureVisitorJourneyRealtime({
    eventKind: "checkout_email_captured",
    funnel: flowName,
    productSurface: "checkout",
    recordStep: "checkout_email_captured",
    intentLevel: "checkout_started",
    extra: {
      pack_id: opts.packId,
      has_payment_intent: Boolean(opts.paymentIntentId),
      gateway: "stripe",
    },
  });

  const payload = {
    email,
    pack_id: opts.packId,
    country: opts.country,
    language: opts.language,
    amount_cents: opts.amountCents,
    currency: opts.currency ?? "usd",
    payment_intent_id: opts.paymentIntentId,
    landing_path: opts.landingPath ?? window.location.pathname,
    flow_name: flowName,
    product_name: opts.productName,
    ...getPostHogIdsForServer(),
    ...getJourneyPayloadForServer(),
  };

  void fetch("/api/capture-stripe-checkout-abandonment", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
    keepalive: true,
  }).catch(() => {});
}